import { FileDropzone, Text, Spacer, Button, Flex } from "@hackthenorth/north";
import React, { useState, useCallback, useMemo, forwardRef } from "react";
import styled from "styled-components";

export enum DropzoneError {
  MIN_AMOUNT = "min-amount",
  MAX_AMOUNT = "max-amount",
  INVALID_FORMAT = "invalid-format",
}

type VideoFileType = "flac" | "mp4" | "ogg" | "webm";

export type AcceptedFileType =
  | "svg"
  | "png"
  | "jpg"
  | "jpeg"
  | "pdf"
  | VideoFileType;

export interface DropzoneProps {
  min?: number;
  max?: number;
  readOnly?: boolean;
  // todo: fix type later
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  label?: any; // any react element
  error?: boolean;
  acceptedFileTypes: AcceptedFileType[];
  onFilesUpload: (files: File[]) => void;
  onError: (error: DropzoneError) => void;
  onBlur: () => void;
}

const FileDropzoneContent = styled(Flex)<{
  dragging: boolean;
  readOnly: boolean;
}>`
  padding: 72px 0;
  background-color: ${({ theme }) => theme.globalConstants.color.greySecondary};
  border-radius: 5px;
  opacity: ${({ dragging }) => (dragging ? 0.5 : 1)};
  transition: filter 200ms;
  filter: ${({ readOnly }) => readOnly && "grayscale(1)"};
`;

/* Prevents capturing from triggering an additional ClickEvent on the Dropzone*/
const CapturingPreventionWrapper = styled.div.attrs({
  onClick: (e) => e.preventDefault(),
})``;

const Dropzone = forwardRef(
  (
    {
      max,
      min,
      readOnly = false,
      label = "Drag and drop file",
      error = false,
      onError,
      onBlur,
      onFilesUpload,
      acceptedFileTypes,
    }: DropzoneProps,
    ref
  ) => {
    const [dragging, setDragging] = useState(false);

    const accept = useMemo(() => {
      return acceptedFileTypes.map((fileType) => `.${fileType}`).join(",");
    }, [acceptedFileTypes]);

    /* See @https://stackoverflow.com/a/17355937 for file validation regex*/
    const isFileValid = useCallback(
      (file: File) => {
        return new RegExp(
          "(" + acceptedFileTypes.join("|").replace(/\./g, "\\.") + ")$"
        ).test(file.type);
      },
      [acceptedFileTypes]
    );

    const onDragChange = useCallback((dragging: boolean) => {
      setDragging(dragging);
    }, []);

    const onDrop = useCallback(
      (files: File[]) => {
        if (min && files.length < min) {
          onError(DropzoneError.MIN_AMOUNT);
          return;
        }

        if (max && files.length > max) {
          onError(DropzoneError.MAX_AMOUNT);
          return;
        }

        if (files.find((file) => !isFileValid(file))) {
          onError(DropzoneError.INVALID_FORMAT);
          return;
        }
        onFilesUpload(files);
      },
      [min, max, isFileValid, onError, onFilesUpload]
    );

    return (
      <CapturingPreventionWrapper>
        <FileDropzone
          className="file-dropzone"
          disabled={false}
          onDragChange={onDragChange}
          options={{
            onDrop,
            noKeyboard: true,
            multiple: min !== 1 || max !== 1,
            accept,
            disabled: readOnly,
            onDropRejected: () => console.debug("rejected"),
          }}
          ref={ref as React.RefObject<HTMLInputElement>}
          readOnly={readOnly}
        >
          <FileDropzoneContent
            column
            justify="center"
            align="center"
            dragging={dragging}
            className="file-dropzone-content"
            readOnly={readOnly}
            onBlur={onBlur}
          >
            <Text
              mods={`medium big bluePrimary2${error ? " error" : ""}`}
              className="file-dropzone-text"
            >
              {label}
            </Text>
            <Spacer height={8} />
            <Text
              mods={`medium big bluePrimary2${error ? " error" : ""}`}
              className="file-dropzone-text"
            >
              OR
            </Text>
            <Spacer height={8} />
            <Button className="file-dropzone-button">
              <Text mods={`medium big bluePrimary2${error ? " error" : ""}`}>
                Select file
              </Text>
            </Button>
          </FileDropzoneContent>
        </FileDropzone>
      </CapturingPreventionWrapper>
    );
  }
);

export default Dropzone;
